/**
 * Best-effort cleanup of the IndexedDB databases used to cache tenant state
 * (the pre-split `cco` database and any `cco-tenant-*` databases). Pure
 * functions on `indexedDB` — no Angular DI — so they can be invoked from
 * both component code and NgRx effects.
 */
import { isCcoDbName, LEGACY_DB_NAME, resolveBootstrapTenantId, tenantDbName } from './tenant-context';

export type DeleteDatabaseOutcome = 'deleted' | 'blocked' | 'error' | 'timeout';

export interface DeleteDatabaseResult {
  name: string;
  outcome: DeleteDatabaseOutcome;
}

/**
 * Lists the IndexedDB database names that should be cleaned up. Prefers the
 * browser's `indexedDB.databases()` enumeration API when available; falls
 * back to the legacy database name plus whichever tenant database can be
 * resolved from storage when it is not (e.g. older browsers).
 */
export async function listCcoDatabaseNames(): Promise<string[]> {
  if (typeof indexedDB.databases === 'function') {
    const databases = await indexedDB.databases();
    return databases
      .map(db => db.name)
      .filter((name): name is string => isCcoDbName(name));
  }

  const names = [LEGACY_DB_NAME];
  const tenantId = resolveBootstrapTenantId();
  if (tenantId) {
    const tenantName = tenantDbName(tenantId);
    if (!names.includes(tenantName))
      names.push(tenantName);
  }

  return names;
}

/**
 * Deletes every cco IndexedDB database (legacy + per-tenant). Never rejects:
 * each database's outcome is reported individually so a failure or timeout
 * on one does not prevent the others from being attempted or reported.
 */
export async function deleteCcoDatabases(timeoutMs = 1000): Promise<DeleteDatabaseResult[]> {
  const names = await listCcoDatabaseNames();
  return Promise.all(names.map(name => deleteDatabaseWithTimeout(name, timeoutMs)));
}

export type ClearDatabaseOutcome = 'cleared' | 'empty' | 'error' | 'timeout';

export interface ClearDatabaseResult {
  name: string;
  outcome: ClearDatabaseOutcome;
}

/**
 * Empties the object stores of every cco IndexedDB database instead of
 * deleting the databases. Use this (not deleteCcoDatabases) wherever other
 * tabs may still hold connections - e.g. logout: a deleteDatabase blocked
 * by another tab's connection stays PENDING in the browser and queues every
 * subsequent open of that database indefinitely, wedging app startup in all
 * new tabs. A clear runs as an ordinary read-write transaction and coexists
 * with open connections. Never rejects.
 */
export async function clearCcoDatabaseContents(timeoutMs = 1000): Promise<ClearDatabaseResult[]> {
  const names = await listCcoDatabaseNames();
  return Promise.all(names.map(name => clearDatabaseWithTimeout(name, timeoutMs)));
}

function clearDatabaseWithTimeout(name: string, timeoutMs: number): Promise<ClearDatabaseResult> {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (outcome: ClearDatabaseOutcome, db?: IDBDatabase) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      db?.close();
      resolve({ name, outcome });
    };

    const timer = setTimeout(() => finish('timeout'), timeoutMs);

    try {
      const request = indexedDB.open(name);
      request.onerror = () => finish('error');
      request.onsuccess = () => {
        const db = request.result;
        const stores = Array.from(db.objectStoreNames);
        if (!stores.length) {
          finish('empty', db);
          return;
        }
        try {
          const transaction = db.transaction(stores, 'readwrite');
          transaction.oncomplete = () => finish('cleared', db);
          transaction.onerror = () => finish('error', db);
          transaction.onabort = () => finish('error', db);
          for (const store of stores) {
            transaction.objectStore(store).clear();
          }
        } catch {
          finish('error', db);
        }
      };
    } catch {
      finish('error');
    }
  });
}

function deleteDatabaseWithTimeout(name: string, timeoutMs: number): Promise<DeleteDatabaseResult> {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (outcome: DeleteDatabaseOutcome) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve({ name, outcome });
    };

    const timer = setTimeout(() => finish('timeout'), timeoutMs);

    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => finish('deleted');
      request.onerror = () => finish('error');
      request.onblocked = () => finish('blocked');
    } catch {
      finish('error');
    }
  });
}
